import { KatalComponent } from '../../shared/base';
import getString from './get-string';

/**
 * @component {kat-statusindicator} KatalStatusindicator A status indicator is a small component that communicates the state of a resource in a compact form that is easily embedded within the resource context.
 * @example Success {"variant": "success", "label":"Success"}
 * @example Error {"variant": "error", "label":"Error"}
 * @example Alert {"variant": "alert", "label":"Alert"}
 * @example Information {"variant": "information", "label":"Information"}
 * @example Pending {"variant": "pending", "label":"Pending"}
 * @example Loading {"variant": "loading", "label":"Loading"}
 * @status Production
 * @theme flo
 */
class KatalStatusindicator extends KatalComponent {
  static get observedAttributes() {
    return ['variant', 'label', 'kat-aria-label'];
  }

  static getIconForType(type) {
    const iconMapping = {
      success: 'checkmark',
      error: 'danger',
      alert: 'warning',
      information: 'info-circle',
      pending: 'clock',
    };

    return iconMapping[type];
  }

  static getIconStringForType(type, label) {
    return getString(`katal-statusindicator-${type}`, { label });
  }

  constructor() {
    super(KatalStatusindicator.observedAttributes);
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  /**
   * @classprop {enum} variant The type of indicator to display to the user
   * @enum {value} success success
   * @enum {value} error error
   * @enum {value} alert alert
   * @enum {value} information information
   * @enum {value} pending pending
   * @enum {value} loading loading
   */
  get variant() {
    return this.getAttribute('variant') || 'success';
  }

  set variant(value) {
    this.setAttribute('variant', value);
  }

  /**
   * @classprop {string} label This is the text that will be displayed to the user
   * @required
   */
  get label() {
    return this.getAttribute('label') || '';
  }

  set label(value) {
    this.setAttribute('label', value);
  }

  /**
   * @classprop {string} kat-aria-label The label used for screen readers.
   */
  get katAriaLabel() {
    return this.getAttribute('kat-aria-label') || '';
  }

  set katAriaLabel(value) {
    this.setAttribute('kat-aria-label', value);
  }

  render() {
    const icon =
      this.variant === 'loading'
        ? `<kat-spinner aria-hidden="true" size="small"></kat-spinner>`
        : `<kat-icon aria-hidden="true" name="${KatalStatusindicator.getIconForType(
            this.variant
          )}" size="small"></kat-icon>`;

    const label = this.katAriaLabel ? this.katAriaLabel : this.label;
    const fullAriaLabel = KatalStatusindicator.getIconStringForType(
      this.variant,
      label
    );

    this.innerHTML = `
            <div role="note" aria-label="${fullAriaLabel}">
                ${icon}
                <span aria-hidden="true">${this.label}</span>
            </div>
        `;
  }

  // Respond to attribute changes.
  attributeChangedCallback() {
    this.render();
  }
}

KatalComponent.register('kat-statusindicator', KatalStatusindicator);
